import {
  serverEventSchema,
  type ConnectRequest,
  type ServerEvent,
} from "@engage/contracts";
import { Centrifuge } from "centrifuge";

/**
 * The browser side of real time — Rule 5's seam for the apps that listen.
 *
 * Separate from the package root on purpose: that is the server's publishing side,
 * and nothing it pulls in belongs in a browser bundle (the participant PWA has a
 * 100 KB budget). No relative imports here either, so the web app can compile this
 * file from source with Turbopack (see @engage/config/eslint/shared-source).
 */

export type RealtimeStatus = "connecting" | "connected" | "disconnected";

export interface RealtimeConnection {
  /**
   * Call an RPC method, resolving with its reply's data. Rejects with a
   * `RealtimeRpcError` carrying the server's code (see the RPC proxy's refusals).
   */
  rpc(method: string, params: Record<string, unknown>): Promise<unknown>;
  close(): void;
}

export class RealtimeRpcError extends Error {
  constructor(
    readonly code: number,
    message: string,
  ) {
    super(message);
    this.name = "RealtimeRpcError";
  }
}

export interface ConnectOptions {
  /** The broker's WebSocket endpoint. Configured per deployment (Rule 7). */
  url: string;
  /**
   * Produces the connect payload, called on EVERY connect and reconnect.
   *
   * A function rather than a value because a presenter's host token lives for five
   * minutes (ADR 0003): a laptop that sleeps through a coffee break must wake up with
   * a fresh credential, not replay an expired one and be refused.
   */
  getConnectData: () => Promise<ConnectRequest>;
  channels: string[];
  onEvent: (event: ServerEvent, channel: string) => void;
  onStatus?: (status: RealtimeStatus) => void;
  /**
   * The broker refused the connection. A code in the 4400s from the connect proxy is
   * final — bad or expired credentials — and the connection stops retrying, since
   * retrying cannot make a refused credential valid.
   */
  onRefused?: (code: number, message: string) => void;
}

export function connectRealtime(options: ConnectOptions): RealtimeConnection {
  const client = new Centrifuge(options.url, {
    getData: options.getConnectData,
  });

  client.on("connecting", () => options.onStatus?.("connecting"));
  client.on("connected", () => options.onStatus?.("connected"));
  client.on("disconnected", () => options.onStatus?.("disconnected"));
  client.on("error", (ctx) => {
    const { code, message } = ctx.error;
    if (ctx.type === "connect" && code >= 4400 && code < 4500) {
      client.disconnect();
      options.onRefused?.(code, message);
    }
  });

  for (const channel of options.channels) {
    const sub = client.newSubscription(channel);
    sub.on("publication", (ctx) => {
      // Parsed, not cast. An event this build does not understand — a newer server
      // mid-deploy — is dropped rather than handed to a switch that cannot handle it.
      const parsed = serverEventSchema.safeParse(ctx.data);
      if (parsed.success) options.onEvent(parsed.data, channel);
    });
    sub.subscribe();
  }

  client.connect();
  return {
    async rpc(method, params) {
      try {
        const reply = await client.rpc(method, params);
        return reply.data;
      } catch (err) {
        const e = err as { code?: number; message?: string };
        throw new RealtimeRpcError(e.code ?? 0, e.message ?? "rpc failed");
      }
    },
    close: () => client.disconnect(),
  };
}
